import React, { useState } from "react";
import { BsTruck, BsTrash } from "react-icons/bs";
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import { setVehicle, resetSetVehicleAPI } from "../../scheduleSlice";
import { Schedule } from "../../../../models";
import "./SetVehicleModal.scss";

interface SetVehicleModalProps {
  isOpen: boolean;
  schedule: Schedule | undefined;
  onClose: () => void;
}

export function SetVehicleModal({
  isOpen,
  schedule,
  onClose,
}: SetVehicleModalProps) {
  const dispatch = useAppDispatch();
  const vehicles = useAppSelector((state) => state.vehicles.vehicles);
  const setVehicleAPI = useAppSelector(
    (state) => state.schedules.setVehicleAPI
  );

  const [formData, setFormData] = useState({
    vehicleId: schedule?.vehicleId || "",
    createDVIRReport: false,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const resetForm = () => {
    setFormData({
      vehicleId: schedule?.vehicleId || "",
      createDVIRReport: false,
    });
    setErrors({});
  };

  React.useEffect(() => {
    if (isOpen && schedule) {
      setFormData({
        vehicleId: schedule.vehicleId || "",
        createDVIRReport: false,
      });
      setErrors({});
    } else if (!isOpen) {
      resetForm();
    }
  }, [isOpen, schedule]);

  // Close modal on successful vehicle assignment
  React.useEffect(() => {
    if (!setVehicleAPI.loading && setVehicleAPI.value && isOpen) {
      handleClose();
    }
  }, [setVehicleAPI.loading, setVehicleAPI.value, isOpen, onClose]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    // No validation needed - vehicle assignment is optional
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!schedule || !validateForm()) {
      return;
    }

    dispatch(
      setVehicle({
        scheduleId: schedule.id,
        vehicleId: formData.vehicleId || null,
        createDVIRReport: formData.createDVIRReport,
      })
    );
  };

  const handleUnassign = () => {
    if (!schedule) return;

    dispatch(
      setVehicle({
        scheduleId: schedule.id,
        vehicleId: null,
        createDVIRReport: false,
      })
    );
  };

  const handleClose = () => {
    resetForm();
    dispatch(resetSetVehicleAPI());
    onClose();
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      // Reset DVIR flag to false when no vehicle is selected
      ...(field === "vehicleId" && !value && { createDVIRReport: false }),
    }));

    // Clear error for this field when user makes changes
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleSubmit();
    } else if (e.key === "Escape") {
      handleClose();
    }
  };

  if (!isOpen || !schedule) return null;

  const currentVehicle = schedule.vehicleId
    ? vehicles.find((v) => v.id === schedule.vehicleId)
    : null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="set-vehicle-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="modal-header">
          <div className="header-content">
            <h3>Set Vehicle</h3>
          </div>
          <button className="close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="modal-form">
          <div className="form-group">
            <label htmlFor="vehicleSelect">Select Vehicle</label>
            <select
              id="vehicleSelect"
              value={formData.vehicleId}
              onChange={(e) => handleInputChange("vehicleId", e.target.value)}
              className={errors.vehicleId ? "error" : ""}
            >
              <option value="">No vehicle assigned</option>
              {vehicles.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.label}
                </option>
              ))}
            </select>
            {errors.vehicleId && (
              <span className="error-message">{errors.vehicleId}</span>
            )}
          </div>

          {currentVehicle && (
            <div className="current-vehicle-info">
              <div className="info-header">
                <BsTruck className="vehicle-icon" />
                <span>Currently Assigned</span>
              </div>
              <div className="vehicle-details">
                <span className="vehicle-name">{currentVehicle.label}</span>
              </div>
            </div>
          )}

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.createDVIRReport}
                onChange={(e) =>
                  handleInputChange("createDVIRReport", e.target.checked)
                }
                disabled={!schedule.vehicleId}
              />
              <span>Create DVIR Report</span>
            </label>
            <div className="help-text">
              {!schedule.vehicleId
                ? "DVIR creation allowed only when changing truck"
                : "Automatically create a DVIR for this assignment"}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div className="left-actions">
            {currentVehicle && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleUnassign}
                disabled={setVehicleAPI.loading}
              >
                <BsTrash className="btn-icon" />
                Unassign
              </button>
            )}
          </div>
          <div className="right-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={setVehicleAPI.loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={setVehicleAPI.loading}
            >
              {setVehicleAPI.loading ? "Updating..." : "Set Vehicle"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
